import React, { useEffect, useState } from 'react';
import AppHeader from '../../../../components/AppHeader.tsx';
import CreateBookingModal from '../../../../components/CreateBookingModal.tsx';
import CustomerModal from '../../../../components/CustomerModal.tsx';
import ConfirmDialog from '../../../../components/ConfirmDialog.tsx';
import RescheduleBookingModal from '../../../../components/RescheduleBookingModal.tsx';
import ImageViewerModal from '../../../../components/ImageViewerModal.tsx';
import AddNoteModal from '../../../../components/AddNoteModal.tsx';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCalendar,
  faCheckCircle,
  faClock,
  faEdit,
  faEuroSign,
  faExclamationTriangle,
  faFilter,
  faPlus,
  faSearch,
  faStar,
  faStickyNote,
  faTimes,
  faTimesCircle,
  faTrash,
  faUser,
} from '@fortawesome/free-solid-svg-icons';
import { useParams } from '@router.ts';
import { useStaffLoV } from '../../../../features/services/hooks/useStaffLoV';
import { useServicesLoV } from '../../../../features/services/hooks/useServicesLoV';
import { useCustomerStore } from '../../../../features/customers/store/useCustomerStore';
import { bookingsWriteApi } from '../../../../api/api';
import { useToast } from '../../../../hooks/useToast';
import type {
  AppCreateCustomerRequest,
  AppUpdateCustomerRequest,
  AttachmentMetadataResponse,
  CustomerBookingHistoryDto,
  CustomerNoteResponse,
} from '../../../../api/generated';
import dayjs from 'dayjs';
import { useNavigate } from 'react-router';

const CustomerDetailPage: React.FC = () => {
  const navigate = useNavigate();
  const { entityId, customerId } = useParams('/app/:entityId/customer-detail/:customerId');
  const [activeTab, setActiveTab] = useState<'history' | 'notes'>('history');
  const [searchTerm, setSearchTerm] = useState('');
  const [isAddNoteModalOpen, setIsAddNoteModalOpen] = useState(false);
  const [isBookingModalOpen, setIsBookingModalOpen] = useState(false);
  const [isCustomerModalOpen, setIsCustomerModalOpen] = useState(false);
  const [isRescheduleModalOpen, setIsRescheduleModalOpen] = useState(false);
  const [isCancelDialogOpen, setIsCancelDialogOpen] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState<CustomerBookingHistoryDto | null>(null);
  const [isImageViewerOpen, setIsImageViewerOpen] = useState(false);
  const [viewerImages, setViewerImages] = useState<Array<{ url: string; filename: string; id: string }>>([]);
  const [viewerCurrentIndex, setViewerCurrentIndex] = useState(0);
  const [isDeleteNoteDialogOpen, setIsDeleteNoteDialogOpen] = useState(false);
  const [noteToDelete, setNoteToDelete] = useState<CustomerNoteResponse | null>(null);
  const [isDeleteAttachmentDialogOpen, setIsDeleteAttachmentDialogOpen] = useState(false);
  const [attachmentToDelete, setAttachmentToDelete] = useState<{ noteId: string; attachmentId: string } | null>(null);

  // Načtení dat pro modal rezervace
  const { staff: staffList } = useStaffLoV(true);
  const { services: servicesList } = useServicesLoV(true);

  // Načtení detailu zákazníka
  const {
    customerDetail,
    customerNotes,
    loading,
    error,
    loadCustomerDetail,
    updateCustomer,
    loadCustomerNotes,
    createCustomerNote,
    deleteCustomerNote,
    uploadNoteAttachment,
    deleteNoteAttachment,
  } = useCustomerStore();

  const toast = useToast();

  useEffect(() => {
    if (customerId) {
      loadCustomerDetail(customerId);
      loadCustomerNotes(customerId);
    }
  }, [customerId, loadCustomerDetail, loadCustomerNotes]);

  // Použij data z API nebo fallback
  const customer = customerDetail || {
    id: customerId,
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    isActive: true,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    totalSpent: 0,
    totalVisits: 0,
    averageRating: 0,
    lastVisitDate: undefined,
    bookingHistory: [],
  };

  const breadcrumb = [{ label: 'Zákazníci' }, { label: 'Seznam zákazníků', href: `/app/${entityId}/customers` }, { label: `${customer.firstName} ${customer.lastName}` }];

  // Historie rezervací z API
  const customerHistory = customer.bookingHistory || [];

  // Filtrovaná data podle vyhledávání
  const filteredHistory = (customerHistory || []).filter(visit => {
    if (!searchTerm) return true;

    const searchLower = searchTerm.toLowerCase();

    return (
      visit.services?.some(s => s?.toLowerCase().includes(searchLower)) ||
      visit.workerName?.toLowerCase().includes(searchLower) ||
      visit.reviewText?.toLowerCase().includes(searchLower) ||
      visit.bookingCode?.toLowerCase().includes(searchLower) ||
      false
    );
  });

  const filteredNotes = customerNotes || [];

  const getStatusIcon = (status: string) => {
    if (!status) return faClock;
    switch (status.toLowerCase()) {
      case 'completed':
        return faCheckCircle;
      case 'cancelled':
        return faTimesCircle;
      case 'no-show':
      case 'no_show':
        return faExclamationTriangle;
      default:
        return faClock;
    }
  };

  const getStatusColor = (status: string) => {
    if (!status) return 'bg-gray-100 text-gray-800';
    switch (status.toLowerCase()) {
      case 'completed':
        return 'bg-green-100 text-green-800';
      case 'cancelled':
        return 'bg-red-100 text-red-800';
      case 'no-show':
      case 'no_show':
        return 'bg-yellow-100 text-yellow-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusText = (status: string) => {
    if (!status) return 'Neznámý';
    switch (status.toLowerCase()) {
      case 'completed':
        return 'Dokončeno';
      case 'cancelled':
        return 'Zrušeno';
      case 'no-show':
      case 'no_show':
        return 'Nedostavil se';
      default:
        return status;
    }
  };

  const renderStars = (rating: number | null) => {
    if (!rating) return <span className="text-gray-400">-</span>;
    return Array.from({ length: 5 }, (_, i) => <FontAwesomeIcon key={i} icon={faStar} className={`w-4 h-4 ${i < rating ? 'text-yellow-400' : 'text-gray-300'}`} />);
  };

  const handleSaveNote = async (noteText: string, images: File[]) => {
    if (!customerId) return;

    try {
      // Vytvoř poznámku
      const createdNote = await createCustomerNote(customerId, {
        noteText,
      });

      // Nahraj obrázky pokud jsou
      if (images.length > 0) {
        for (const image of images) {
          await uploadNoteAttachment(customerId, createdNote.id, image);
        }
      }

      toast.success('Úspěch!', 'Poznámka byla přidána');

      // Reload poznámek
      await loadCustomerNotes(customerId);

      setIsAddNoteModalOpen(false);
    } catch (error) {
      console.error('Chyba při přidávání poznámky:', error);
      toast.error('Chyba!', 'Nepodařilo se přidat poznámku');
      throw error;
    }
  };

  const handleEditCustomer = () => {
    setIsCustomerModalOpen(true);
  };

  const handleSaveCustomer = async (data: AppCreateCustomerRequest | AppUpdateCustomerRequest, _customerId?: string) => {
    if (!customerId) return;

    try {
      await updateCustomer(customerId, data as AppUpdateCustomerRequest);
      toast.success('Úspěch!', 'Údaje zákazníka byly aktualizovány');
      await loadCustomerDetail(customerId); // Reload detail
      setIsCustomerModalOpen(false);
    } catch (error) {
      console.error('Chyba při úpravě zákazníka:', error);
      toast.error('Chyba!', 'Nepodařilo se aktualizovat zákazníka');
      throw error;
    }
  };

  const handleRescheduleBooking = (booking: CustomerBookingHistoryDto) => {
    setSelectedBooking(booking);
    setIsRescheduleModalOpen(true);
  };

  const handleCancelBooking = (booking: CustomerBookingHistoryDto) => {
    setSelectedBooking(booking);
    setIsCancelDialogOpen(true);
  };

  const handleConfirmCancelBooking = async () => {
    if (!selectedBooking) return;

    try {
      await bookingsWriteApi.cancelBooking({
        bookingId: selectedBooking.bookingId,
        cancelBookingRequest: {
          cancellationReason: 'Zrušeno správcem',
        },
      });

      toast.success('Úspěch!', `Rezervace ${selectedBooking.bookingCode} byla zrušena`);

      // Reload customer detail
      if (customerId) {
        await loadCustomerDetail(customerId);
      }

      setIsCancelDialogOpen(false);
      setSelectedBooking(null);
    } catch (error) {
      console.error('Chyba při rušení rezervace:', error);
      toast.error('Chyba!', 'Nepodařilo se zrušit rezervaci');
      throw error;
    }
  };

  const handleRescheduleSuccess = async () => {
    // Reload customer detail after successful reschedule
    if (customerId) {
      await loadCustomerDetail(customerId);
    }
    setIsRescheduleModalOpen(false);
    setSelectedBooking(null);
  };

  const handleOpenImageViewer = (attachments: AttachmentMetadataResponse[], index: number) => {
    const images = attachments.map(att => ({
      url: att.url,
      filename: att.filename,
      id: att.id,
    }));
    setViewerImages(images);
    setViewerCurrentIndex(index);
    setIsImageViewerOpen(true);
  };

  const handleNavigateImage = (direction: 'prev' | 'next') => {
    if (direction === 'prev' && viewerCurrentIndex > 0) {
      setViewerCurrentIndex(viewerCurrentIndex - 1);
    } else if (direction === 'next' && viewerCurrentIndex < viewerImages.length - 1) {
      setViewerCurrentIndex(viewerCurrentIndex + 1);
    }
  };

  const handleOpenDeleteNoteDialog = (note: CustomerNoteResponse) => {
    setNoteToDelete(note);
    setIsDeleteNoteDialogOpen(true);
  };

  const handleConfirmDeleteNote = async () => {
    if (!noteToDelete || !customerId) return;

    try {
      await deleteCustomerNote(customerId, noteToDelete.id);
      await loadCustomerNotes(customerId);
      toast.success('Úspěch!', 'Poznámka byla smazána');
      setIsDeleteNoteDialogOpen(false);
      setNoteToDelete(null);
    } catch (error) {
      toast.error('Chyba!', 'Nepodařilo se smazat poznámku');
      throw error;
    }
  };

  const handleOpenDeleteAttachmentDialog = (noteId: string, attachmentId: string) => {
    setAttachmentToDelete({ noteId, attachmentId });
    setIsDeleteAttachmentDialogOpen(true);
  };

  const handleConfirmDeleteAttachment = async () => {
    if (!attachmentToDelete || !customerId) return;

    try {
      await deleteNoteAttachment(customerId, attachmentToDelete.noteId, attachmentToDelete.attachmentId);
      await loadCustomerNotes(customerId);
      toast.success('Úspěch!', 'Příloha byla smazána');
      setIsDeleteAttachmentDialogOpen(false);
      setAttachmentToDelete(null);
    } catch (error) {
      toast.error('Chyba!', 'Nepodařilo se smazat přílohu');
      throw error;
    }
  };

  // Loading state
  if (loading && !customerDetail) {
    return (
      <div className="h-full flex flex-col">
        <AppHeader title="Detail zákazníka" breadcrumb={breadcrumb} />
        <div className="flex-1 flex items-center justify-center bg-gray-50">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Načítání detailu zákazníka...</p>
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="h-full flex flex-col">
        <AppHeader title="Detail zákazníka" breadcrumb={breadcrumb} />
        <div className="flex-1 flex items-center justify-center bg-gray-50">
          <div className="text-center">
            <div className="text-red-600 mb-4">
              <FontAwesomeIcon icon={faExclamationTriangle} className="text-6xl mb-4" />
              <p className="text-lg font-medium">Chyba při načítání</p>
              <p className="text-sm mt-2">{error}</p>
            </div>
            <button onClick={() => navigate(`/app/${entityId}/customers`)} className="mt-4 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700">
              Zpět na seznam
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col">
      <AppHeader title={`${customer.firstName} ${customer.lastName}`} breadcrumb={breadcrumb} />

      <div className="flex-1 overflow-y-auto bg-gray-50">
        <div className="p-6 space-y-6">
          {/* Customer Header */}
          <section className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center">
                  <FontAwesomeIcon icon={faUser} className="w-10 h-10 text-green-600" />
                </div>
                <div className="flex-1">
                  <h1 className="text-2xl font-semibold text-gray-900">
                    {customer.firstName} {customer.lastName}
                  </h1>
                  <p className="text-gray-600">{customer.email}</p>
                  <p className="text-gray-600">{customer.phone}</p>
                  <div className="flex items-center space-x-4 mt-2">
                    <span className={`px-3 py-1 text-sm rounded-full ${customer.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                      {customer.isActive ? 'Aktivní' : 'Neaktivní'}
                    </span>
                    <span className="text-sm text-gray-500">Zákazník od {new Date(customer.createdAt).toLocaleDateString('cs-CZ')}</span>
                  </div>
                </div>
              </div>
              <div className="flex items-center space-x-3">
                <button onClick={handleEditCustomer} className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors">
                  <FontAwesomeIcon icon={faEdit} className="mr-2" />
                  Upravit zákazníka
                </button>
                <button className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors" onClick={() => setIsBookingModalOpen(true)}>
                  <FontAwesomeIcon icon={faCalendar} className="mr-2" />
                  Nová rezervace
                </button>
              </div>
            </div>
          </section>

          {/* Stats Cards */}
          <section className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <div className="bg-white rounded-lg shadow-sm p-6">
              <div className="flex items-center">
                <div className="p-3 bg-blue-50 rounded-lg">
                  <FontAwesomeIcon icon={faCalendar} className="text-xl text-blue-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Celkem návštěv</p>
                  <p className="text-2xl font-bold text-gray-900">{customer.totalVisits}</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-lg shadow-sm p-6">
              <div className="flex items-center">
                <div className="p-3 bg-green-50 rounded-lg">
                  <FontAwesomeIcon icon={faEuroSign} className="text-xl text-green-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Celkem utraceno</p>
                  <p className="text-2xl font-bold text-gray-900">{customer.totalSpent.toLocaleString()} Kč</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-lg shadow-sm p-6">
              <div className="flex items-center">
                <div className="p-3 bg-yellow-50 rounded-lg">
                  <FontAwesomeIcon icon={faStar} className="text-xl text-yellow-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Průměrné hodnocení</p>
                  <p className="text-2xl font-bold text-gray-900">{customer.averageRating ? `${customer.averageRating}/5` : 'N/A'}</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-lg shadow-sm p-6">
              <div className="flex items-center">
                <div className="p-3 bg-purple-50 rounded-lg">
                  <FontAwesomeIcon icon={faClock} className="text-xl text-purple-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Poslední návštěva</p>
                  <p className="text-2xl font-bold text-gray-900">{customer.lastVisitDate ? new Date(customer.lastVisitDate).toLocaleDateString('cs-CZ') : 'Žádná'}</p>
                </div>
              </div>
            </div>
          </section>

          {/* Tabs */}
          <section className="bg-white rounded-lg shadow-sm">
            <div className="border-b border-gray-200">
              <nav className="-mb-px flex space-x-8 px-6">
                <button
                  onClick={() => setActiveTab('history')}
                  className={`py-4 px-1 border-b-2 font-medium text-sm ${
                    activeTab === 'history' ? 'border-green-500 text-green-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <FontAwesomeIcon icon={faCalendar} className="w-4 h-4 mr-2" />
                  Historie návštěv
                </button>
                <button
                  onClick={() => setActiveTab('notes')}
                  className={`py-4 px-1 border-b-2 font-medium text-sm ${
                    activeTab === 'notes' ? 'border-green-500 text-green-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <FontAwesomeIcon icon={faStickyNote} className="w-4 h-4 mr-2" />
                  Poznámky
                </button>
              </nav>
            </div>

            <div className="p-6">
              {/* Search and Actions - only for history tab */}
              {activeTab === 'history' && (
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center space-x-4">
                    <div className="flex-1 relative max-w-md">
                      <FontAwesomeIcon icon={faSearch} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                      <input
                        type="text"
                        placeholder="Hledat v historii..."
                        value={searchTerm}
                        onChange={e => setSearchTerm(e.target.value)}
                        className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500/20 focus:border-green-500"
                      />
                    </div>
                    <button className="flex items-center px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50">
                      <FontAwesomeIcon icon={faFilter} className="w-4 h-4 mr-2" />
                      Filtry
                    </button>
                  </div>
                </div>
              )}

              {/* Actions for notes tab */}
              {activeTab === 'notes' && (
                <div className="flex items-center justify-end mb-6">
                  <button onClick={() => setIsAddNoteModalOpen(true)} className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors">
                    <FontAwesomeIcon icon={faPlus} className="mr-2" />
                    Nová poznámka
                  </button>
                </div>
              )}

              {/* History Tab Content */}
              {activeTab === 'history' && (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Datum a čas</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Služba</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Zaměstnanec</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Cena</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Hodnocení</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Stav</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Akce</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {filteredHistory.map(visit => {
                        const visitDate = new Date(visit.date);
                        return (
                          <tr key={visit.bookingId} className="hover:bg-gray-50">
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-sm text-gray-900">{visitDate.toLocaleDateString('cs-CZ')}</div>
                              <div className="text-sm text-gray-500 flex items-center">
                                <FontAwesomeIcon icon={faClock} className="w-3 h-3 mr-1" />
                                {visitDate.toLocaleTimeString('cs-CZ', { hour: '2-digit', minute: '2-digit' })}
                              </div>
                            </td>
                            <td className="px-6 py-4">
                              <div className="text-sm text-gray-900">{visit.services?.join(', ') || '-'}</div>
                              <div className="text-sm text-gray-500">{visit.bookingCode}</div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{visit.workerName || '-'}</td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                              {visit.totalPrice?.toLocaleString()} {visit.currency || 'Kč'}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="flex items-center">{renderStars(visit.overallRating || null)}</div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(visit.state)}`}>
                                <FontAwesomeIcon icon={getStatusIcon(visit.state)} className="w-3 h-3 mr-1" />
                                {getStatusText(visit.state)}
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                              <div className="flex items-center space-x-2">
                                <button
                                  onClick={() => handleRescheduleBooking(visit)}
                                  className="text-blue-600 hover:text-blue-900 p-1"
                                  title="Přeplánovat"
                                  disabled={visit.state?.toLowerCase() === 'cancelled' || visit.state?.toLowerCase() === 'completed'}
                                >
                                  <FontAwesomeIcon icon={faCalendar} className="w-4 h-4" />
                                </button>
                                <button
                                  onClick={() => handleCancelBooking(visit)}
                                  className="text-red-600 hover:text-red-900 p-1"
                                  title="Zrušit rezervaci"
                                  disabled={visit.state?.toLowerCase() === 'cancelled'}
                                >
                                  <FontAwesomeIcon icon={faTimes} className="w-4 h-4" />
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                  {filteredHistory.length === 0 && (
                    <div className="text-center py-12">
                      <FontAwesomeIcon icon={faCalendar} className="w-16 h-16 text-gray-400 mb-4" />
                      <h3 className="text-lg font-medium text-gray-900 mb-2">Žádná historie</h3>
                      <p className="text-gray-600">Pro tohoto zákazníka zatím není žádná historie návštěv.</p>
                    </div>
                  )}
                </div>
              )}

              {/* Notes Tab Content */}
              {activeTab === 'notes' && (
                <div className="space-y-4">
                  {filteredNotes.map(note => (
                    <div key={note.id} className="bg-white border border-gray-200 rounded-lg p-5 hover:shadow-md transition-shadow">
                      {/* Kdo a kdy */}
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center text-sm text-gray-600">
                          <FontAwesomeIcon icon={faUser} className="w-4 h-4 mr-2 text-gray-400" />
                          <span className="font-medium text-gray-900">{note.createdBy}</span>
                          <span className="mx-2">•</span>
                          <FontAwesomeIcon icon={faCalendar} className="w-4 h-4 mr-2 text-gray-400" />
                          <span>{new Date(note.createdAt).toLocaleDateString('cs-CZ')}</span>
                          {note.updatedAt && note.updatedAt !== note.createdAt && <span className="ml-2 text-xs text-gray-500">(upraveno)</span>}
                        </div>
                        <button onClick={() => handleOpenDeleteNoteDialog(note)} className="text-red-600 hover:text-red-900 p-2" title="Smazat poznámku">
                          <FontAwesomeIcon icon={faTrash} className="w-4 h-4" />
                        </button>
                      </div>

                      {/* Text poznámky */}
                      <p className="text-gray-700 text-sm whitespace-pre-wrap leading-relaxed mb-3">{note.noteText}</p>

                      {/* Fotky - pod textem */}
                      {note.attachments && note.attachments.length > 0 && (
                        <div className="mt-3">
                          <div className="flex flex-wrap gap-2">
                            {note.attachments.map((attachment, index) => (
                              <div key={attachment.id} className="relative group">
                                <div className="w-20 h-20 rounded-lg overflow-hidden border-2 border-gray-200 hover:border-green-500 transition-colors cursor-pointer">
                                  <img
                                    src={attachment.url}
                                    alt={attachment.filename}
                                    className="w-full h-full object-cover"
                                    onClick={() => handleOpenImageViewer(note.attachments!, index)}
                                  />
                                </div>
                                {/* Delete button */}
                                <button
                                  onClick={e => {
                                    e.stopPropagation();
                                    handleOpenDeleteAttachmentDialog(note.id, attachment.id);
                                  }}
                                  className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1.5 opacity-0 group-hover:opacity-100 hover:bg-red-600 transition-all shadow-lg"
                                  title="Smazat přílohu"
                                >
                                  <FontAwesomeIcon icon={faTimes} className="w-3 h-3" />
                                </button>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                  {filteredNotes.length === 0 && (
                    <div className="text-center py-12">
                      <FontAwesomeIcon icon={faStickyNote} className="w-16 h-16 text-gray-400 mb-4" />
                      <h3 className="text-lg font-medium text-gray-900 mb-2">Žádné poznámky</h3>
                      <p className="text-gray-600 mb-4">Pro tohoto zákazníka zatím nejsou žádné poznámky.</p>
                      <button onClick={() => setIsAddNoteModalOpen(true)} className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors">
                        <FontAwesomeIcon icon={faPlus} className="mr-2" />
                        Přidat první poznámku
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          </section>
        </div>
      </div>

      {/* Modal pro vytvoření nové rezervace */}
      {isBookingModalOpen && (
        <CreateBookingModal
          isOpen={isBookingModalOpen}
          onClose={() => setIsBookingModalOpen(false)}
          selectedDate={dayjs().format('YYYY-MM-DD')}
          salonId={entityId || ''}
          staffList={staffList.map(s => ({ id: s.id, name: s.name }))}
          servicesList={servicesList.map(s => ({
            id: s.id,
            name: s.name,
            price: s.price || 0,
            duration: s.duration || 0,
          }))}
          preSelectedCustomer={{
            firstName: customer.firstName,
            lastName: customer.lastName,
            phone: customer.phone,
            email: customer.email,
          }}
          onFinish={async () => {
            // Po dokončení rezervace reload detailu
            setIsBookingModalOpen(false);
            if (customerId) {
              await loadCustomerDetail(customerId);
            }
          }}
        />
      )}

      {/* Modal pro úpravu zákazníka */}
      <CustomerModal isOpen={isCustomerModalOpen} onClose={() => setIsCustomerModalOpen(false)} mode="edit" customer={customer} onSave={handleSaveCustomer} />

      {/* Modal pro přeplánování rezervace */}
      {isRescheduleModalOpen && selectedBooking && (
        <RescheduleBookingModal
          isOpen={isRescheduleModalOpen}
          onClose={() => {
            setIsRescheduleModalOpen(false);
            setSelectedBooking(null);
          }}
          bookingId={selectedBooking.bookingId}
          currentDate={selectedBooking.date}
          currentTime={dayjs(selectedBooking.date).format('HH:mm')}
          currentStaffName={selectedBooking.workerName || ''}
          staffList={staffList}
          onSuccess={handleRescheduleSuccess}
        />
      )}

      {/* Dialog pro zrušení rezervace */}
      <ConfirmDialog
        isOpen={isCancelDialogOpen}
        onClose={() => {
          setIsCancelDialogOpen(false);
          setSelectedBooking(null);
        }}
        onConfirm={handleConfirmCancelBooking}
        title="Zrušit rezervaci?"
        message={
          selectedBooking
            ? `Opravdu chcete zrušit rezervaci ${selectedBooking.bookingCode} na ${dayjs(selectedBooking.date).format('D.M.YYYY')}? Zákazník může být informován emailem.`
            : ''
        }
        confirmText="Ano, zrušit rezervaci"
        cancelText="Zpět"
        variant="warning"
      />

      {/* Image Viewer Modal */}
      <ImageViewerModal
        isOpen={isImageViewerOpen}
        onClose={() => setIsImageViewerOpen(false)}
        images={viewerImages}
        currentIndex={viewerCurrentIndex}
        onNavigate={handleNavigateImage}
      />

      {/* Dialog pro smazání poznámky */}
      <ConfirmDialog
        isOpen={isDeleteNoteDialogOpen}
        onClose={() => {
          setIsDeleteNoteDialogOpen(false);
          setNoteToDelete(null);
        }}
        onConfirm={handleConfirmDeleteNote}
        title="Smazat poznámku?"
        message="Opravdu chcete smazat tuto poznámku včetně všech příloh? Tato akce je nevratná."
        confirmText="Ano, smazat"
        cancelText="Zrušit"
        variant="danger"
      />

      {/* Dialog pro smazání přílohy */}
      <ConfirmDialog
        isOpen={isDeleteAttachmentDialogOpen}
        onClose={() => {
          setIsDeleteAttachmentDialogOpen(false);
          setAttachmentToDelete(null);
        }}
        onConfirm={handleConfirmDeleteAttachment}
        title="Smazat přílohu?"
        message="Opravdu chcete smazat tuto přílohu? Tato akce je nevratná."
        confirmText="Ano, smazat"
        cancelText="Zrušit"
        variant="danger"
      />

      {/* Modal pro přidání poznámky */}
      <AddNoteModal
        isOpen={isAddNoteModalOpen}
        onClose={() => setIsAddNoteModalOpen(false)}
        onSave={handleSaveNote}
        customerName={`${customer.firstName} ${customer.lastName}`}
      />
    </div>
  );
};

export default CustomerDetailPage;
